import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import useStore, { useData } from "../../hooks/useStore";
import { ListRenderer } from "./data/List";
import { getDataAt, getSchemaValType, sortData } from "./utils";
import { T } from "../../global";
import { ObjectRenderer } from "./data/Object";
import { ValueRenderer } from "./data/Value";
import { DbRenderer } from "./data/Database";
import { Reorder } from "framer-motion";
import { View } from "./View";
import { PointerRenderer } from "./data/Pointer";

export const renderData = (
  infoObjectName,
  path,
  name,
  data,
  schema,
  _options,
  _is
) => {
  const options = {
    hideKeys: false,
    isPrimary: false,
    isPreviewValue: false,
    ..._options,
  };

  const is = {
    view: false,
    floatingWindow: false,
    previewView: false,
    ..._is,
  };

  const dataType = getSchemaValType(schema);
  const pathKey = path.length > 0 ? path : "ROOT"; // how did we get here?

  const rendererProps = {
    infoObjectName,
    path,
    name,
    data,
    schema,
    options,
    is,
  };

  const key = `${infoObjectName}-${pathKey}`;

  switch (dataType) {
    case T.LIST:
      return <ListRenderer key={key} {...rendererProps} />;
    case T.DB:
      // return <DbRenderer key={key} {...rendererProps} />
      // if (options.hideKeys)
      // return <DbRenderer key={key} {...rendererProps} />
      return <ListRenderer key={key} {...rendererProps} />;

    case T.OBJ:
      return <ObjectRenderer key={key} {...rendererProps} />;

    case T.VAL:
      return <ValueRenderer key={key} {...rendererProps} />;

    case T.PT:
      return <PointerRenderer key={key} {...rendererProps} />;

    default:
      return null;
  }
};

/* -------------------------------------------------------------------------- */

export const HighlightContext = createContext(null);

export const UiRenderer = () => {
  const allViews = useStore((state) => state.allViews);
  const xray = useStore((state) => state.xray); // meta-structure or actual ui

  const allData = useData();
  const allSchema = useStore((state) => state.getInfoObjects());
  // > highlight context
  const [highlightedInfoObject, setHighlightedInfoObject] = useState({
    infoObject: null,
    id: null, // ?
  });
  const highlightContext = useMemo(
    () => ({
      highlightedInfoObject,
      setHighlightedInfoObject,
    }),
    [highlightedInfoObject]
  );

  // < highlight context

  const rendererRef = useRef(null);
  const [viewOrders, setViewOrders] = useState(
    allViews.map((view) => view.vid)
  );

  useEffect(() => {
    const newViews = allViews.filter((view) => !viewOrders.includes(view.vid));
    const removedViews = viewOrders.filter(
      (vid) => !allViews.some((view) => view.vid === vid)
    );

    if (newViews.length > 0 || removedViews.length > 0) {
      setViewOrders((prevOrders) => {
        const updatedOrders = prevOrders.filter(
          (vid) => !removedViews.includes(vid)
        );
        return [...updatedOrders, ...newViews.map((view) => view.vid)];
      });
    }
  }, [allViews, viewOrders]);

  const handleReorder = useCallback((newOrder) => {
    setViewOrders(newOrder);
  }, []);

  const renderAllViews = useMemo(() => {
    return viewOrders.map((viewId) => {
      const view = allViews.find((view) => view.vid === viewId);
      if (!view) return null;

      const { infoObject, path } = view;

      const dataAtPath = getDataAt(allData, allSchema, infoObject, path);
      const viewData = sortData(dataAtPath);

      return (
        <View
          key={`view-${view.infoObject}-${view.path}`}
          view={view}
          viewData={dataAtPath}
        />
      );
    });
  }, [viewOrders, allViews, allData, allSchema]);

  return (
    <Reorder.Group
      key={`ui-renderer-${xray}`}
      layoutScroll
      axis="x"
      values={viewOrders}
      onReorder={handleReorder}
      className="ui-renderer"
      ref={rendererRef}
      data-state={xray ? "xray" : "ui"}
    >
      <HighlightContext.Provider value={highlightContext}>
        {renderAllViews}
      </HighlightContext.Provider>
    </Reorder.Group>
  );
};

export const HostInfoObjectContext = createContext();
